import type { Key } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Select } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import type { ContentFilterDraft, DomainOption, FilterTarget, FilterValidationError } from "../types";

import { DomainCombobox } from "./DomainCombobox";
import { FilterCardWrapper } from "./FilterCardWrapper";
import { LabelWithTooltip } from "./LabelWithTooltip";

const { carbon } = colorsPalettes;

const StyledTextField = styled(TextField)`
    flex: 1;
`;

export type FilterCardProps = {
    draft: ContentFilterDraft;
    availableOptions: DomainOption[];
    error?: FilterValidationError;
    onChange: (id: string, patch: Partial<ContentFilterDraft>) => void;
    onDelete: (id: string) => void;
};

export const FilterCard = ({ draft, availableOptions, error, onChange, onDelete }: FilterCardProps) => {
    const { translate } = useTranslate();

    const filterTargetOptions: { id: FilterTarget; label: string }[] = [
        { id: 0, label: translate("activation.settings.news.filter.filterTarget.news") },
        { id: 1, label: translate("activation.settings.news.filter.filterTarget.everything") },
    ];

    const handleDomainSelect = (domain: string) => {
        onChange(draft.id, { domain });
    };

    const handleFilterTargetSelect = (key: Key | null) => {
        if (key !== null) {
            onChange(draft.id, { filterTarget: Number(key) as FilterTarget });
        }
    };

    const handlePositiveChange = (value: string) => {
        onChange(draft.id, { positiveKeywords: value });
    };

    const handleNegativeChange = (value: string) => {
        onChange(draft.id, { negativeKeywords: value });
    };

    const handleDelete = () => {
        onDelete(draft.id);
    };

    return (
        <FilterCardWrapper data-automation={generateAutomationName("settings", ["news", "filter", draft.id])}>
            <Flex
                gap="16px"
                alignItems="flex-end"
            >
                <Stack
                    gap="4px"
                    flex="1"
                    minWidth="0"
                >
                    <Text
                        fontVariant="caption"
                        color={carbon[300]}
                    >
                        {translate("common.domain")}
                    </Text>
                    <DomainCombobox
                        options={availableOptions}
                        selectedValue={draft.domain || null}
                        onSelect={handleDomainSelect}
                        isInvalid={!!error?.domain}
                        errorMessage={error?.domain}
                        dataAutomation={generateAutomationName("settings", ["news", "filter", draft.id, "domain"])}
                    />
                </Stack>

                <Stack
                    gap="4px"
                    flex="1"
                    minWidth="0"
                >
                    <Text
                        fontVariant="caption"
                        color={carbon[300]}
                    >
                        {translate("activation.settings.news.filter.filterTarget.label")}
                    </Text>
                    <Flex
                        gap="16px"
                        alignItems="center"
                    >
                        <Stack
                            flex="1"
                            minWidth="0"
                        >
                            <Select
                                width="100%"
                                items={filterTargetOptions}
                                selectedKey={draft.filterTarget}
                                onSelectionChange={handleFilterTargetSelect}
                                data-automation={generateAutomationName("settings", [
                                    "news",
                                    "filter",
                                    draft.id,
                                    "filter-target",
                                ])}
                            >
                                {(option) => (
                                    <Select.Item
                                        id={option.id}
                                        textValue={option.label}
                                    >
                                        {option.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </Stack>

                        <Button
                            type="flatWarning"
                            onClick={handleDelete}
                            dataAutomation={generateAutomationName("settings", ["news", "filter", draft.id, "delete"])}
                        >
                            {translate("common.delete")}
                        </Button>
                    </Flex>
                </Stack>
            </Flex>

            <Stack
                gap="16px"
                flex="1"
                minWidth="0"
            >
                <Flex
                    gap="16px"
                    justifyContent="space-between"
                >
                    <StyledTextField
                        label={
                            <LabelWithTooltip
                                label={translate("activation.settings.news.filter.positiveKeywords.label")}
                                tooltipContent={translate("activation.settings.news.filter.positiveKeywords.tooltip")}
                            />
                        }
                        value={draft.positiveKeywords}
                        onChange={handlePositiveChange}
                        placeholder={translate("activation.settings.news.filter.positiveKeywords.placeholder")}
                        isInvalid={!!error?.positiveKeywords || !!error?.keywords}
                        errorMessage={error?.positiveKeywords}
                    />
                    <StyledTextField
                        label={
                            <LabelWithTooltip
                                label={translate("activation.settings.news.filter.negativeKeywords.label")}
                                tooltipContent={translate("activation.settings.news.filter.negativeKeywords.tooltip")}
                            />
                        }
                        value={draft.negativeKeywords}
                        onChange={handleNegativeChange}
                        placeholder={translate("activation.settings.news.filter.negativeKeywords.placeholder")}
                        isInvalid={!!error?.negativeKeywords || !!error?.keywords}
                        errorMessage={error?.negativeKeywords}
                    />
                </Flex>
                {error?.keywords ? (
                    <Text
                        textAlign="center"
                        fontVariant="caption"
                        color={colorsPalettes.red["400_P"]}
                    >
                        {error.keywords}
                    </Text>
                ) : null}
            </Stack>
        </FilterCardWrapper>
    );
};
